import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Package } from 'lucide-react';
import { DashboardNav } from '../components/dashboard/DashboardNav';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useData } from '../contexts/DataContext';
import { formatZAR } from '../utils/currency';
import { ProductForm } from '../components/admin/ProductForm';
import { Product } from '../types/models';
export function AdminProducts() {
  const {
    products,
    deleteProduct
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(undefined);
  const filteredProducts = products.filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.category.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteProduct(id);
    }
  };
  const handleAddNew = () => {
    setEditingProduct(undefined);
    setIsFormOpen(true);
  };
  return <div className="min-h-screen bg-rose-50/30 flex" data-id="element-1572">
      <DashboardNav data-id="element-1573" />

      <main className="flex-1 p-4 lg:p-8 overflow-y-auto h-screen" data-id="element-1574">
        <div className="max-w-7xl mx-auto space-y-8" data-id="element-1575">
          <header className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1576">
            <div data-id="element-1577">
              <h1 className="text-2xl font-bold text-gray-900 font-serif" data-id="element-1578">
                Products
              </h1>
              <p className="text-gray-500" data-id="element-1579">Manage your product inventory</p>
            </div>
            <Button onClick={handleAddNew} leftIcon={<Plus size={20} data-id="element-1581" />} data-id="element-1580">
              Add Product
            </Button>
          </header>

          <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden" data-id="element-1582">
            <div className="p-4 border-b border-rose-100 flex gap-4" data-id="element-1583">
              <div className="relative flex-1 max-w-md" data-id="element-1584">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-1585" />
                <input type="text" placeholder="Search products..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-1586" />
              </div>
            </div>

            <div className="overflow-x-auto" data-id="element-1587">
              <table className="w-full" data-id="element-1588">
                <thead className="bg-rose-50/50" data-id="element-1589">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1590">
                    <th className="px-6 py-4" data-id="element-1591">Product</th>
                    <th className="px-6 py-4" data-id="element-1592">Category</th>
                    <th className="px-6 py-4" data-id="element-1593">Price</th>
                    <th className="px-6 py-4" data-id="element-1594">Stock</th>
                    <th className="px-6 py-4 text-right" data-id="element-1595">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-50" data-id="element-1596">
                  {filteredProducts.map(product => <tr key={product.id} className="hover:bg-rose-50/30 transition-colors" data-id="element-1597">
                      <td className="px-6 py-4" data-id="element-1598">
                        <div className="flex items-center" data-id="element-1599">
                          <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-1600">
                            <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-id="element-1601" />
                          </div>
                          <div className="ml-4" data-id="element-1602">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1603">
                              {product.name}
                            </div>
                            {product.isNew && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-800" data-id="element-1604">
                                New
                              </span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1605">
                        {product.category}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-1606">
                        {formatZAR(product.price)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1607">
                        {product.stock}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2" data-id="element-1608">
                        <button onClick={() => handleEdit(product)} className="text-gray-400 hover:text-rose-500 transition-colors" data-id="element-1609">
                          <Edit2 size={18} data-id="element-1610" />
                        </button>
                        <button onClick={() => handleDelete(product.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-1611">
                          <Trash2 size={18} data-id="element-1612" />
                        </button>
                      </td>
                    </tr>)}
                  {filteredProducts.length === 0 && <tr data-id="element-1613">
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-1614">
                        <Package className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-1615" />
                        <p data-id="element-1616">No products found</p>
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <ProductForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} product={editingProduct} data-id="element-1617" />
    </div>;
}